import { useState } from "react";
import { LockIcon } from "vega-icons";

import { NotificationPreferencesSkeleton } from "@vega-core/components/NotificationPreferences/NotificationPreferencesSkeleton";
import { Button } from "@vega-core/components/shadcn/button";
import { Switch } from "@vega-core/components/Switch/Switch";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";

import type {
  NotificationPreferencesProps,
  PreferenceToggleProps,
} from "./types";

export {
  type NotificationLockConfig,
  type NotificationPreferencesProps,
} from "./types";

export const NotificationPreferences = ({
  preferences,
  isLoading,
  onPreferenceChange,
  disabled = false,
  lockConfig = [],
  isUpdating = false,
}: NotificationPreferencesProps) => {
  const [targetPreference, setTargetPreference] = useState<string | null>(null);

  const handlePreferenceChange = (id: string, enabled: boolean) => {
    setTargetPreference(id);
    onPreferenceChange(id, enabled);
  };

  if (isLoading) {
    return <NotificationPreferencesSkeleton />;
  }

  if (!preferences?.length) {
    return null;
  }

  return (
    <div className="flex flex-col divide-y-1 divide-border/primary w-full">
      {preferences.map((preference) => (
        <PreferenceToggle
          disabled={disabled}
          isUpdating={targetPreference === preference.id && isUpdating}
          key={preference.id}
          lockConfig={lockConfig}
          onPreferenceChange={handlePreferenceChange}
          preference={preference}
        />
      ))}
    </div>
  );
};

const PreferenceToggle = ({
  preference,
  onPreferenceChange,
  disabled,
  lockConfig,
  isUpdating = false,
}: PreferenceToggleProps) => {
  const [active, setActive] = useState(preference.enabled);

  const handleToggle = () => {
    setActive(!active);
    onPreferenceChange(preference.id, !active);
  };

  const lockInfo = lockConfig.find((config) => config.type === preference.type);

  const isLocked = !preference.isEditable || !!lockInfo || disabled;

  const tooltipMessage = !preference.isEditable
    ? m.settings_notificationPreferences_notEditable()
    : lockInfo?.message || m.settings_capitalActivity_tooltip();

  return (
    <div className="w-full py-4">
      {/* Mobile layout - column style */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <p className="body-base-medium text-text/primary">
              {preference.name}
            </p>
            {isLocked ? (
              <Tooltip
                className="max-w-44 text-start"
                clickToOpen
                content={tooltipMessage}
              >
                <Button
                  className="hover:bg-transparent"
                  data-testid={`lock-icon-button-mobile-${preference.id}`}
                  variant="ghost"
                >
                  <LockIcon className="size-6" />
                </Button>
              </Tooltip>
            ) : null}
          </div>
          <p className="text-text/secondary body-small-regular">
            {preference.description}
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <Switch
            checked={preference.enabled}
            disabled={isLocked || isUpdating}
            id={`switch-mobile-${preference.id}`}
            isLoading={isUpdating}
            onChange={handleToggle}
          />
        </div>
      </div>

      {/* Desktop layout - horizontal style */}
      <div className="hidden md:flex items-center gap-3 min-h-14">
        <div className="flex flex-col gap-1 flex-1">
          <p className="body-base-medium text-text/primary">
            {preference.name}
          </p>
          <p className="text-text/secondary body-small-regular">
            {preference.description}
          </p>
        </div>
        <div className="flex gap-3 items-center">
          {isLocked ? (
            <Tooltip
              className="max-w-44 text-start"
              clickToOpen
              content={tooltipMessage}
            >
              <Button
                className="hover:bg-transparent"
                data-testid={`lock-icon-button-desktop-${preference.id}`}
                variant="ghost"
              >
                <LockIcon className="size-6" />
              </Button>
            </Tooltip>
          ) : null}
          <Switch
            checked={preference.enabled}
            disabled={isLocked || isUpdating}
            id={`switch-desktop-${preference.id}`}
            isLoading={isUpdating}
            onChange={handleToggle}
          />
        </div>
      </div>
    </div>
  );
};
